// Helpers for the 14-day free trial.
//
// The backend decides trial eligibility on its own - the frontend never asks for
// a trial. `POST /subscriptions/create` simply comes back in one of three shapes
// and these helpers flatten it into something the screens can branch on:
//
//   status === 'trialing'   -> trial started, nothing to pay. When
//                              requires_setup is true a SetupIntent client
//                              secret comes with it so the card can be saved
//                              (skipping it means the subscription cancels when
//                              the trial ends).
//   requires_payment: true  -> paid subscription, confirm the PaymentIntent.
//   otherwise               -> already active, no Stripe.js step needed.

export const TRIAL_DAYS = 14;

const TRIALING_STATUSES = ['trialing', 'trial', 'in_trial'];
const ACTIVE_STATUSES = ['active', 'complete', 'completed'];

const toBool = (value) => value === true || value === 1 || value === '1' || value === 'true';

export const isTrialingStatus = (status) =>
  TRIALING_STATUSES.includes(String(status || '').trim().toLowerCase());

export const isActiveStatus = (status) =>
  ACTIVE_STATUSES.includes(String(status || '').trim().toLowerCase());

// Subscription is usable (dashboard access, no payment wall) while trialing too.
export const isLiveSubscriptionStatus = (status) =>
  isActiveStatus(status) || isTrialingStatus(status);

/**
 * Flattens whatever /subscriptions/create (or /change-plan) returned into the
 * fields the payment screens branch on. Both snake_case and camelCase are
 * accepted, and the payload may or may not be wrapped in `data`.
 */
export const readSubscriptionResponse = (response) => {
  const data = response?.data || response || {};

  const status = data.status || data.subscription_status || data.subscriptionStatus || '';
  const clientSecret = data.client_secret || data.clientSecret || '';
  const requiresSetup = toBool(data.requires_setup ?? data.requiresSetup);
  const requiresPayment = toBool(data.requires_payment ?? data.requiresPayment);
  const trialing = isTrialingStatus(status);

  return {
    status,
    clientSecret,
    requiresSetup,
    requiresPayment,
    trialing,
    subscriptionId: data.subscription_id || data.subscriptionId || '',
    trialEnd: data.trial_end || data.trialEnd || null,
    // What the screen has to do next:
    //   'setup'  -> stripe.confirmCardSetup (card saved for after the trial)
    //   'pay'    -> stripe.confirmCardPayment
    //   'done'   -> nothing left to confirm
    nextAction: trialing
      ? (requiresSetup && clientSecret ? 'setup' : 'done')
      : (requiresPayment && clientSecret ? 'pay' : 'done'),
  };
};

/**
 * Trial/billing timestamps arrive in a few different shapes depending on the
 * endpoint: Stripe seconds (1771481871), millisecond strings ("1771481871000")
 * or an ISO date. Returns a Date, or null when it cannot be read.
 */
export const parseApiDate = (value) => {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const numeric = typeof value === 'number' ? value : Number(String(value).trim());
  if (Number.isFinite(numeric) && String(value).trim() !== '') {
    // Anything below ~1e12 is seconds, not milliseconds.
    const ms = Math.abs(numeric) < 1e12 ? numeric * 1000 : numeric;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatApiDate = (value) => {
  const date = parseApiDate(value);
  if (!date) return 'N/A';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

/**
 * Whole days between now and `trialEnd`, rounded up so the last partial day
 * still reads as "1 day left". Returns null when there is no usable date and 0
 * once the trial is over.
 */
export const getTrialDaysLeft = (trialEnd) => {
  const end = parseApiDate(trialEnd);
  if (!end) return null;
  const msLeft = end.getTime() - Date.now();
  if (msLeft <= 0) return 0;
  return Math.ceil(msLeft / (24 * 60 * 60 * 1000));
};

/** "Trial - 9 days left" for the subscription badge. */
export const formatTrialLabel = (trialEnd) => {
  const daysLeft = getTrialDaysLeft(trialEnd);
  if (daysLeft === null) return 'Trial';
  if (daysLeft <= 0) return 'Trial ended';
  return `Trial - ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`;
};
